import {
  Avatar,
  Box,
  FormControl,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import BrowseGalleryIcon from "@mui/icons-material/BrowseGallery";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Chart, registerables } from "chart.js";

import {
  getStandardTopics,
  getTopicSpecializationPageData,
} from "../../../api/specialization/TopicSpecializationAPI";
import WordCloudWrapper from "../../../components/WordCloudWrapper";
import { useTranslation } from "react-i18next";
import { errorNoti } from "../../../util/SnackbarMessage";
import { PageHeader } from "../../../components/PageHeader";
import { SentimentGauge } from "../../../components/SentimentGauge";
import { getAllSites } from "../../../api/newsspecs/sites/SitesSpecsAPI";
import { WordBarElement } from "../../../components/WordBarElement";
import { singleField, sumByField } from "../../../util/ArrayUtil";
import { curveLineOptions } from "../../../util/ChartStyleUtil";
import { Line } from "react-chartjs-2";
import { HighlightNumber } from "../../../components/HighlightNumber";
import SiteIcon from "../../../util/SiteIcon";
import NumberBox from "../../../components/NumberBox";
import { dateNow } from "../../../util/StringUtil";
import { TopicIcon } from "../../../util/TopicIcon";

Chart.register(...registerables);

export default function TopicSpecializationPage() {
  const [topics, setTopics] = useState([]);
  const [chosenTopic, setChosenTopic] = useState("Kinh tế");
  const [activeSites, setActiveSites] = useState([]);
  const [words, setWords] = useState([]);
  const [sites, setSites] = useState([]);
  const [time, setTime] = useState({ hour: 12, day: 28 });
  const [sentiment, setSentiment] = useState(null);
  const [oldKeywords, setOldKeywords] = useState([]);
  const [positiveKeywords, setPositiveKeywords] = useState([]);
  const [negativeKeywords, setNegativeKeywords] = useState([]);
  const [recentOccurrences, setRecentOccurrences] = useState([]);
  const [activeAuthors, setActiveAuthors] = useState([]);
  const { t } = useTranslation(["common", "error", "topic", "site"]);
  const [averageTimeOnHeadlines, setAverageTimeOnHeadlines] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getAllSites()
      .then((res) => {
        setSites(res);
      })
      .catch((err) => {
        var noti = errorNoti(err);
        enqueueSnackbar(t("error:" + noti.message), noti.option);
      });
    getStandardTopics()
      .then((res) => setTopics(res))
      .catch((err) => {
        var noti = errorNoti(err);
        enqueueSnackbar(t("error:" + noti.message), noti.option);
      });
  }, []);

  useEffect(() => {
    getTopicSpecializationPageData(chosenTopic, time.hour)
      .then((res) => {
        setWords(res["popularKeywords"]);
        if (res["oldPopularKeywords"].length > 1) {
          setOldKeywords(JSON.parse(res["oldPopularKeywords"][1]["keywords"]));
        } else
          setOldKeywords(JSON.parse(res["oldPopularKeywords"][0]["keywords"]));
        setSentiment(res["overallSentiment"]);
        setPositiveKeywords(res["positiveKeywords"]);
        setNegativeKeywords(res["negativeKeywords"]);
        setActiveSites(res["activeSites"]);
        setRecentOccurrences(res["recentOccurences"]);
        setAverageTimeOnHeadlines(res["averageHeadlinesTime"]);
        setActiveAuthors(res["activeAuthors"]);
      })
      .catch((err) => {
        var noti = errorNoti(err);
        enqueueSnackbar(t("error:" + noti.message), noti.option);
      });
  }, [chosenTopic, time.hour]);

  var maxAuthorArticlesCount =
    activeAuthors.length > 0 ? activeAuthors[0]["points"] : 1;
  
  var articlesCountToday = recentOccurrences.length > 0 ? sumByField(recentOccurrences.slice(-24, -1).filter((e: any) => {
    return e.date == dateNow();
  }), "count") : 0;

  var displayAuthors = activeAuthors.slice(0, 6).map((author) => {
    var siteData = sites.filter((a) => {
      return a["name"] == author["source"];
    });
    var siteIcon = SiteIcon(siteData[0]["domain"]);
    return (
      <ListItem style={{ marginBottom: "4px" }}>
        <ListItemAvatar>
          <Avatar alt={author["source"]} src={siteIcon} />
        </ListItemAvatar>
        <ListItemText
          primary={author["author"]}
          secondary={author["points"] + " " + t("common:articles")}
        ></ListItemText>
        <Box sx={{ width: "45%", marginRight: "10px" }}>
          <LinearProgress
            variant="determinate"
            color={"info"}
            value={(100 * author["points"]) / maxAuthorArticlesCount}
          />
        </Box>
      </ListItem>
    );
  });

  var recentOccurencesData = recentOccurrences
    .sort((a: any, b: any) => {
      if (a.date > b.date) {
        return 1;
      }
      if (a.date == b.date && a.hour > b.hour) {
        return 1;
      }
      if (a.date == b.date && a.hour == b.hour) {
        return 0;
      }
      return -1;
    })
    .slice(-120, -1)
    .map((res: any) => {
      return {
        time: res.hour + ":00 " + res.date,
        count: res.count,
      };
    });

  var recentOccurencesDataset = {
    labels: singleField(recentOccurencesData, "time"),
    datasets: [
      {
        data: singleField(recentOccurencesData, "count"),
        label: "Số lần được nhắc đến",
        fill: true,
        backgroundColor: "rgba(75,192,192,0.2)",
        borderColor: "rgba(75,192,192,1)",
      },
    ],
  };

  const recentOccurencesChartOptions = curveLineOptions();
  recentOccurencesChartOptions.scales = {
    maintainAspectRatio: false,
    display: false,
    x: {
      min: 0,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    y: {
      min: 0,
      grid: {
        display: false,
      },
    },
  };
  recentOccurencesChartOptions.plugins.legend.display = false;

  var activeSitesList = activeSites.slice(0, 5).map((site) => {
    var siteData = sites.filter((a) => {
      return a["name"] == site["site"];
    });
    var siteIcon = SiteIcon(siteData[0]["domain"]);
    return (
      <ListItem className="slide-from-bottom">
        <ListItemAvatar>
          <Avatar alt={site["site"]} src={siteIcon} />
        </ListItemAvatar>
        <Link
          href={siteData[0] ? siteData[0]["domain"] : "https://trendz.vn"}
          underline="none"
          target={"_blank"}
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            flexDirection: "row",
            paddingTop: "0px",
            paddingBottom: "0px",
          }}
        >
          <ListItemText
            primary={site["site"]}
            primaryTypographyProps={{
              fontSize: "25px",
              fontWeight: 500,
              align: "left",
            }}
            secondary={site["count"] + " " + t("common:articles")}
          />
        </Link>
      </ListItem>
    );
  });

  var highestPositive =
    positiveKeywords.length > 0 ? positiveKeywords[0]["points"] : 1;

  var positiveKeywordsList = positiveKeywords.slice(0, 2).map((keyword) => {
    var barLength = highestPositive
      ? (keyword["points"] / highestPositive) * 100
      : 1;
    return (
      <WordBarElement
        word={keyword["token"]}
        scaledNumber={barLength}
        icon={<StarPurple500Icon color="warning" />}
      />
    );
  });

  var highestNegative =
    negativeKeywords.length > 0 ? negativeKeywords[0]["points"] : 1;

  var negativeKeywordsList = negativeKeywords.slice(0, 2).map((keyword) => {
    var barLength = highestNegative
      ? (keyword["points"] / highestNegative) * 100
      : 1;
    return (
      <WordBarElement
        word={keyword["token"]}
        scaledNumber={barLength}
        color="error"
        icon={<WhatshotIcon color="error" />}
      />
    );
  });
  var oldRanks = oldKeywords.map((oldKeyword, index) => {
    return {
      rank: index + 1,
      keyword: (oldKeyword["token"] as any).toString().toLowerCase(),
    };
  });
  const buttons = topics.map(function (topic) {
    return (
      <MenuItem value={topic["standardTopic"]}>
        {topic["standardTopic"]}
      </MenuItem>
    );
  });
  return (
    <Grid container alignItems="center" justifyContent="center">
      <PageHeader
        text={t("topic:topic_stats")}
        supplement={
          <FormControl
            style={{ height: "35px", display: "inline" }}
            id="topic-form-control"
          >
            <Select
              value={chosenTopic}
              // variant="standard"
              onChange={(event: SelectChangeEvent) => {
                setChosenTopic(event.target.value as string);
              }}
              style={{
                width: "120px !important",
                fontSize: "22px",
                height: "35px",
                marginLeft: "10px",
                fontWeight: 500,
              }}
            >
              {buttons}
            </Select>
            <TextField
              // variant="standard"
              id="wordcloud-box-input"
              sx={{ width: "60px", height: "35px", margin: "0 8px" }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 0,
                max: 999,
                defaultValue: 12,
                value: time.hour,
                onChange: (event) => {
                  setTime({
                    hour: parseInt(event.currentTarget.value) || 1,
                    day: time.day,
                  });
                },
              }}
            />
          </FormControl>
        }
        textAfterSupplement={t("common:recent_hours")}
      ></PageHeader>
      <Grid container xs={12} spacing={0} style={{ marginTop: "10px" }}>
        <Grid item xs={0.75}></Grid>
        <Grid
          item
          xs={3}
          className="grid-border white slide-from-left"
          textAlign={"center"}
          padding={2}
        >
          <NumberBox
            title={t("keyword:average_headline_time")}
            variant={"h3"}
            number={
              Math.round(averageTimeOnHeadlines) + " " + t("common:minutes")
            }
            icon={
              <BrowseGalleryIcon
                sx={{ width: "40px", height: "40px", color: "#81d4fa" }}
              />
            }
            subtitle={t("common:derived_from_this_week")}
          />
        </Grid>
        <Grid item xs={0.25}></Grid>
        <Grid
          item
          xs={2.5}
          className="grid-border white slide-from-left"
          textAlign={"center"}
          padding={2}
        >
          <NumberBox
            title={t("site:articles_count_today")}
            variant={"h3"}
            number={
              Math.round(articlesCountToday)
            }
            icon={
              TopicIcon({name: chosenTopic, width: 40})
            }
            subtitle={t("common:derived_from_today")}
          />
        </Grid>
        <Grid item xs={0.25}></Grid>
        <Grid
          item
          xs={4.5}
          className="white slide-from-right grid-border"
          padding={2}
        >
          <Typography variant="h5" align="left" fontWeight={600}>
            {t("topic:recent_occurrences_hourly")}
          </Typography>
          <Line
            data={recentOccurencesDataset}
            options={recentOccurencesChartOptions}
            height={50}
          ></Line>
        </Grid>
        <Grid item xs={0.5}></Grid>
      </Grid>
      <Grid container xs={12} marginTop={4}>
        <Grid item xs={0.75} />
        <Grid item xs={3} className="grid-border white slide-from-left">
          <Typography
            variant="h5"
            align="center"
            marginLeft={"15px"}
            marginTop={"10px"}
            fontWeight={600}
            marginBottom={"15px"}
          >
            {t("common:most_active_authors")}
          </Typography>
          <List>{displayAuthors}</List>
        </Grid>
        <Grid item xs={0.25} />
        <Grid item xs={4} className="grid-border slide-from-bottom">
          <WordCloudWrapper
            words={words}
            time={time.hour}
            oldRanks={oldRanks}
            sentiment={sentiment}
            noTable={true}
          ></WordCloudWrapper>
        </Grid>
        <Grid item xs={0.25} />
        <Grid
          item
          xs={3}
          className="white slide-from-right grid-border"
          sx={{ marginTop: "20px" }}
        >
          {sentiment ? (
            <Typography
              variant="h5"
              align="left"
              marginLeft={"15px"}
              marginTop={"10px"}
              fontWeight={600}
              marginBottom={"15px"}
            >
              {t("common:overall_sentiment") + " " + t("common:today")}
            </Typography>
          ) : (
            ""
          )}
          {sentiment ? <SentimentGauge sentiment={sentiment} /> : ""}

          {positiveKeywordsList.length > 0 ? (
            <Typography
              variant="h5"
              align="left"
              marginLeft={"15px"}
              marginTop={"10px"}
              fontWeight={600}
              marginBottom={"5px"}
              className="slide-from-right"
            >
              {t("keyword:most_positive_keywords") + " " + t("common:today")}
            </Typography>
          ) : (
            ""
          )}

          <List
            sx={{ display: "flex", flexDirection: "column", paddingTop: "0px" }}
          >
            {positiveKeywordsList}
          </List>
          {negativeKeywordsList.length > 0 ? (
            <Typography
              variant="h5"
              align="left"
              marginLeft={"15px"}
              marginTop={"10px"}
              fontWeight={600}
              marginBottom={"5px"}
              className="slide-from-right"
            >
              {t("keyword:most_negative_keywords") + " " + t("common:today")}
            </Typography>
          ) : (
            ""
          )}

          <List
            sx={{ display: "flex", flexDirection: "column", paddingTop: "0px" }}
          >
            {negativeKeywordsList}
          </List>
        </Grid>

        <Grid item xs={0.75} />
        {activeSitesList.length > 0 ? (
          <Grid container xs={12} marginTop={4} className="slide-from-bottom ">
            <Grid item xs={0.75}></Grid>
            <Grid item xs={10.5} className="white grid-border">
              <Typography
                variant="h5"
                align="left"
                marginLeft={"15px"}
                marginTop={"10px"}
                fontWeight={600}
              >
                {t("site:most_active_sites")}
              </Typography>
              <List
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  paddingTop: "0px",
                }}
              >
                {activeSitesList}
              </List>
            </Grid>
            <Grid item xs={0.75}></Grid>
          </Grid>
        ) : (
          ""
        )}
      </Grid>
    </Grid>
  );
}
